import { Link } from "react-router-dom";
import { SavedFacilityButton } from "@/components/board/SavedFacilityButton";
import { Status } from "@/components/system";
import {
  cleanText,
  listingAvailability,
  listingFocus,
  listingPractice,
  listingTypeLabel,
  sourceCheckedLabel,
} from "@/lib/listing-display";
import type { Facility } from "@/lib/public-directory";
import { compactGpReportedLabel } from "@/lib/wait-reports";

/** One listing, identical on the directory, specialty, area and home pages. */
export function ListingCard({ facility, headingLevel = 3 }: { facility: Facility; headingLevel?: 2 | 3 }) {
  const isSpecialist = facility.facility_type === "specialist";
  const profilePath = `/directory/specialist/${facility.slug}`;
  const availability = listingAvailability(facility);
  const focus = listingFocus(facility);
  const practice = listingPractice(facility);
  const suburb = cleanText(facility.address_json.suburb);
  const checked = sourceCheckedLabel(facility);
  const eyebrow = cleanText(facility.address_json.specialty) ?? cleanText(facility.address_json.category) ?? listingTypeLabel(facility.facility_type);
  const Heading = headingLevel === 2 ? "h2" : "h3";

  return (
    <article className="ds-card ds-listing">
      <div className="ds-listing__top">
        <span className="ds-eyebrow">{eyebrow}</span>
        <Status tone={availability.tone}>{availability.label}</Status>
      </div>

      <div className="ds-listing__who">
        <Heading className="ds-listing__name">
          {isSpecialist ? <Link to={profilePath}>{facility.name}</Link> : facility.name}
        </Heading>
        {practice ? <p className="ds-small">{practice}</p> : null}
      </div>

      <div className="ds-listing__body">
        {focus.length > 0 ? (
          <ul className="ds-listing__tags" aria-label="Focus areas">
            {focus.slice(0, 3).map((item) => (
              <li key={item} className="ds-tag">{item}</li>
            ))}
            {focus.length > 3 ? <li className="ds-tag">+{focus.length - 3} more</li> : null}
          </ul>
        ) : (
          <p className="ds-fine">Focus areas not published</p>
        )}
        {availability.detail ? <p className="ds-fine">{availability.detail}</p> : null}
        {facility.gpReported ? <p className="ds-fine">{compactGpReportedLabel(facility.gpReported)}</p> : null}
      </div>

      <div className="ds-listing__foot">
        <span className="ds-fine">{[suburb, checked].filter(Boolean).join(" · ") || "Canberra"}</span>
        <span className="ds-cluster" style={{ gap: 12 }}>
          {isSpecialist ? <SavedFacilityButton facility={facility} compact /> : null}
          {isSpecialist ? (
            <Link className="ds-listing__go" to={profilePath} aria-label={`View profile: ${facility.name}`}>
              View profile
            </Link>
          ) : null}
        </span>
      </div>
    </article>
  );
}

export function ListingGrid({ children }: { children: React.ReactNode }) {
  return <div className="ds-grid ds-listing-grid">{children}</div>;
}

export function ListingSkeletons({ count = 6 }: { count?: number }) {
  return (
    <div className="ds-grid" aria-label="Loading listings">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="ds-skeleton" style={{ height: 220 }} />
      ))}
    </div>
  );
}
